import React, { useCallback, useEffect, useRef, useState } from "react";
import { useUser } from "@/shared/lib/hooks/useUser/useUser";
import {
    IMPERSONATION_STATE_KEY,
    USER_LOCALSTORAGE_KEY,
} from "@/shared/config/localstorage";
import {
    exitImpersonation,
    readImpersonationState,
    userForCurrentState,
    IMPERSONATION_CHANGED_EVENT,
    type ImpersonationState,
} from "../lib/impersonationSession";

function fmt(msLeft: number): string {
    const total = Math.max(0, Math.floor(msLeft / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Global "You are viewing as <user> — mm:ss — Exit" banner (INT). Mounted once,
 * above the router, so it survives the admin→user swap. Live countdown, auto-exit
 * at expiry, cross-tab sync, one-click Exit. Renders nothing when not active.
 */
export const ImpersonationBanner: React.FC = () => {
    const { setUser } = useUser();
    const applyUser = useCallback(
        (u: Parameters<NonNullable<typeof setUser>>[0]) => setUser?.(u),
        [setUser],
    );
    const [state, setState] = useState<ImpersonationState | null>(() =>
        readImpersonationState(),
    );
    const [now, setNow] = useState<number>(() => Date.now());
    // Был ли просмотр активен до пришедшего известия из другой вкладки.
    const activeRef = useRef<boolean>(!!readImpersonationState()?.active);
    const lastSyncedUser = useRef<string>("");
    const exitingRef = useRef<boolean>(false);

    const doExit = useCallback(() => {
        if (exitingRef.current) return;
        exitingRef.current = true;
        void exitImpersonation(applyUser).finally(() => {
            exitingRef.current = false;
            setState(null);
            // Страница перечитывается целиком (#1700). Смена сеанса возвращает
            // админа себе самому, но уже открытый экран остаётся с данными
            // того, за кого смотрели: форма профиля держала чужое имя и
            // сохранила бы его уже в свою учётную запись.
            window.location.reload();
        });
    }, [applyUser]);

    // Cross-tab (storage) + same-tab (custom event) refresh. storage events fire
    // only in OTHER tabs, so the initiating tab relies on the custom event.
    useEffect(() => {
        const refresh = (syncUser: boolean) => {
            const next = readImpersonationState();
            const wasActive = activeRef.current;
            activeRef.current = !!next?.active;
            setState(next);
            if (!syncUser) return;
            // Вышли в соседней вкладке — здесь на экране тоже чужие данные.
            if (wasActive && !next?.active) {
                window.location.reload();
                return;
            }
            const u = userForCurrentState();
            const ser = JSON.stringify(u ?? "");
            if (ser !== lastSyncedUser.current) {
                lastSyncedUser.current = ser;
                applyUser(u);
            }
        };
        const onStorage = (e: StorageEvent) => {
            if (
                e.key !== null &&
                e.key !== IMPERSONATION_STATE_KEY &&
                e.key !== USER_LOCALSTORAGE_KEY
            ) {
                return;
            }
            refresh(true);
        };
        const onLocal = () => refresh(false);
        window.addEventListener("storage", onStorage);
        window.addEventListener(IMPERSONATION_CHANGED_EVENT, onLocal);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener(IMPERSONATION_CHANGED_EVENT, onLocal);
        };
    }, [applyUser]);

    // 1s tick for the countdown + auto-exit at expiry.
    useEffect(() => {
        if (!state?.active) return;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [state?.active]);

    useEffect(() => {
        if (state?.active && now >= state.expiresAt) {
            doExit();
        }
    }, [now, state, doExit]);

    if (!state?.active) return null;

    const msLeft = state.expiresAt - now;

    return (
        <div
            role="alert"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 2147483647,
                background: "#b71c1c",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                padding: "8px 16px",
                fontSize: 14,
                fontWeight: 600,
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                fontFamily:
                    "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
            }}
        >
            <span aria-hidden>👁️</span>
            <span>
                Viewing as user: <b>{state.targetEmail}</b> · read-only
            </span>
            <span style={{ fontVariantNumeric: "tabular-nums", opacity: 0.9 }}>
                {fmt(msLeft)} left
            </span>
            <button
                type="button"
                onClick={doExit}
                style={{
                    background: "#fff",
                    color: "#b71c1c",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 14px",
                    fontWeight: 700,
                    cursor: "pointer",
                }}
            >
                Exit
            </button>
        </div>
    );
};
